import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useEffect } from "react";

// Range values
const TARGET_TOLERANCE = 5;
const INDICATOR_HALF_SPAN = 30;

// Indicator colors
const COLOR_OK = "#00e676";
const COLOR_BAD = "#f75353";
const COLOR_NEUTRAL = "#fff";

// Bar geometry (px)
const BAR_HEIGHT = 180;
export const TILT_INDICATOR_HEIGHT = BAR_HEIGHT;

// Ring behavior
const RING_BORDER = 3;

// Bubble behavior -- Doing algebra to solve for its size
const BUBBLE_TO_RING_RATIO = 0.65;
const K = (BUBBLE_TO_RING_RATIO * TARGET_TOLERANCE) / INDICATOR_HALF_SPAN;
const BUBBLE_SIZE = (K * BAR_HEIGHT) / (1 + K);
const MAX_TRAVEL = (BAR_HEIGHT - BUBBLE_SIZE) / 2;
const PX_PER_DEG = MAX_TRAVEL / INDICATOR_HALF_SPAN;

// Target ring's diameter indicating the real tolerance
const ZONE_SIZE = 2 * TARGET_TOLERANCE * PX_PER_DEG;

// Bubble-bar-style tilt indicator
export const TiltIndicator = ({ tilt, targetTilt, onScanReady }) => {
  // Tilt value
  const hasTilt = typeof tilt === "number";
  const delta = hasTilt ? tilt - targetTilt : 0;

  // Statuses
  const withinTarget = hasTilt && Math.abs(delta) <= TARGET_TOLERANCE;
  const overTarget = hasTilt && !withinTarget && delta > 0;

  // Status UI
  const statusText = withinTarget
    ? "Ready"
    : overTarget
      ? "Tilt down"
      : "Tilt up";
  const textColor = withinTarget ? COLOR_OK : COLOR_BAD;
  const bubbleColor = withinTarget ? COLOR_OK : COLOR_NEUTRAL;

  // Indicator UI
  // Positive delta moves the bubble up, negative down.
  const clamped = Math.max(
    -INDICATOR_HALF_SPAN,
    Math.min(INDICATOR_HALF_SPAN, delta),
  );
  const bubbleOffset = -clamped * PX_PER_DEG;

  // Tell the parent whether the scan may start.
  useEffect(() => {
    onScanReady(withinTarget);
  }, [withinTarget, onScanReady]);

  // The component
  return (
    <Box
      role="img"
      aria-label="Device tilt indicator"
      sx={{
        position: "relative",
        width: ZONE_SIZE,
        height: BAR_HEIGHT,
        pointerEvents: "none",
        // Keeps the dashes readable over a bright camera preview
        filter: "drop-shadow(0 0 2px rgba(0,0,0,0.8))",
      }}
    >
      {/* The line */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          width: 6,
          transform: "translateX(-50%)",
          borderRadius: 3,
          backgroundImage:
            "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.3) 75%, rgba(255,255,255,0) 100%)",
        }}
      />

      {/* Target ring, centered on the line */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          boxSizing: "border-box",
          width: ZONE_SIZE,
          height: ZONE_SIZE,
          marginTop: `${-ZONE_SIZE / 2}px`,
          marginLeft: `${-ZONE_SIZE / 2}px`,
          borderRadius: "50%",
          border: `${RING_BORDER}px solid ${COLOR_OK}`,
        }}
      />

      {/* The bubble: centered by margins, moved by the transform */}
      {hasTilt && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: BUBBLE_SIZE,
            height: BUBBLE_SIZE,
            marginTop: `${-BUBBLE_SIZE / 2}px`,
            marginLeft: `${-BUBBLE_SIZE / 2}px`,
            borderRadius: "50%",
            backgroundColor: bubbleColor,
            boxShadow: "0 0 6px rgba(0,0,0,0.6)",
            transform: `translateY(${bubbleOffset}px)`,
            transition: "transform 0.15s ease-out, background-color 0.2s",
          }}
        />
      )}

      {/* Status text */}
      {statusText && (
        <Typography
          sx={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            mb: 1,
            whiteSpace: "nowrap",
            color: textColor,
            fontWeight: "bold",
            fontSize: 20,
            textShadow: [
              "-1px -1px 0 rgba(0,0,0,0.2)",
              "0 -1px 0 rgba(0,0,0,0.2)",
              "1px -1px 0 rgba(0,0,0,0.2)",
              "-1px 0 0 rgba(0,0,0,0.2)",
              "1px 0 0 rgba(0,0,0,0.2)",
              "-1px 1px 0 rgba(0,0,0,0.2)",
              "0 1px 0 rgba(0,0,0,0.2)",
              "1px 1px 0 rgba(0,0,0,0.2)",
            ].join(", "),
          }}
        >
          {statusText}
        </Typography>
      )}
    </Box>
  );
};

TiltIndicator.propTypes = {
  tilt: PropTypes.number,
  targetTilt: PropTypes.number.isRequired,
  onScanReady: PropTypes.func.isRequired,
};
